import {ApplicationConfig, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection} from '@angular/core';
import {provideRouter, withRouterConfig} from '@angular/router';
import {routes} from './app.routes';
import {provideStore, Store} from '@ngrx/store';
import {provideHttpClient, withInterceptors} from '@angular/common/http';
import {TranslocoHttpLoader} from './shared/translation/loader/transloco-loader';
import {provideTransloco} from '@jsverse/transloco';
import {provideEffects} from '@ngrx/effects';
import {provideRouterStore, routerReducer} from '@ngrx/router-store';
import {provideStoreDevtools} from '@ngrx/store-devtools';
import {appReducer} from './store/app.reducers';
import {newsFeatureKey, newsReducer} from './modules/news/store/news.reducers';
import {NewsEffects} from './modules/news/store/news.effects';
import {AuthenticationService} from './modules/authentication/services/authentication.service';
import {environment} from '@env/environment';
import {authenticationInterceptor} from './core/interceptors/authentication.interceptor';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {AuthenticationEffects} from './modules/authentication/store/authentication.effects';
import {ProfileEffects} from './modules/profile/store/profile.effects';
import {provideServiceWorker} from '@angular/service-worker';
import {TranslationService} from './shared/translation/translation.service';
import {forkJoin} from 'rxjs';
import {loadSession} from './modules/authentication/store/authentication.actions';
import {layoutFeatureKey, layoutReducer} from './modules/collection/components/layout/store/layout.reducers';
import {locationFeatureKey, locationReducer} from './modules/location/store/location.reducers';
import {LocationEffects} from './modules/location/store/location.effects';
import {clearStateMetaReducer} from './store/meta-reducer';
import {languageFeatureKey, languageReducer} from './shared/language/store/language.reducers';
import {LanguageEffects} from './shared/language/store/language.effects';
import {initLanguage} from './shared/language/store/language.actions';
import {notificationFeatureKey, notificationReducer} from './modules/notification/store/notification.reducers';
import {NotificationEffects} from './modules/notification/store/notification.effects';
import {flowFeatureKey, flowReducer} from './modules/flow/store/flow.reducers';
import {FlowEffects} from './modules/flow/store/flow.effects';
import {
  requestMaintenanceFeatureKey,
  requestMaintenanceReducer
} from './modules/profile/pages/request-maintenance/store/request-maintenance.reducers';
import {RequestMaintenanceEffects} from './modules/profile/pages/request-maintenance/store/request-maintenance.effects';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({eventCoalescing: true}),
    provideRouter(
      routes,
      withRouterConfig({
        paramsInheritanceStrategy: "always",
        onSameUrlNavigation: "reload"
      })
    ),
    provideStore(
      {
        ...appReducer,
        router: routerReducer,
        [newsFeatureKey]: newsReducer,
        [locationFeatureKey]: locationReducer,
        [layoutFeatureKey]: layoutReducer,
        [languageFeatureKey]: languageReducer,
        [requestMaintenanceFeatureKey]: requestMaintenanceReducer,
        [notificationFeatureKey]: notificationReducer,
        [flowFeatureKey]: flowReducer
      },
      {
        metaReducers: [clearStateMetaReducer]
      }
    ),
    provideEffects([
      AuthenticationEffects,
      ProfileEffects,
      RequestMaintenanceEffects,
      NewsEffects,
      LocationEffects,
      LanguageEffects,
      NotificationEffects,
      FlowEffects
    ]),
    provideHttpClient(withInterceptors([authenticationInterceptor])),
    provideTransloco({
      config: {
        availableLangs: environment.availableLangs,
        defaultLang: environment.defaultLang,
        fallbackLang: environment.defaultLang,
        reRenderOnLangChange: true,
        prodMode: !isDevMode()
      },
      loader: TranslocoHttpLoader
    }),
    provideAppInitializer(() => {
      const auth = inject(AuthenticationService);
      const translation = inject(TranslationService);
      const store = inject(Store);
      store.dispatch(loadSession());
      store.dispatch(initLanguage());
      return forkJoin([
        auth.initAuthFlow(),
        translation.retrieveAndSetTranslations()
      ]);
    }),
    provideRouterStore(),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode()
    }),
    provideAnimationsAsync(),
    provideServiceWorker("ngsw-worker.js", {
      enabled: !isDevMode(),
      registrationStrategy: "registerWhenStable:30000"
    }),
    provideAnimationsAsync()
  ]
};
